/**
 * Note repository — centralized row mapping and query helpers.
 *
 * All NoteRow → LocalNote conversions go through rowToLocalNote().
 * Never define row mappers for notes elsewhere in the codebase.
 */

import { noteTitleForDisplay } from '@caliu/utils'
import type { SyncStatus } from '../../types'
import type { LocalNote } from '../db'
import { query } from '../sqlite'
import type { NoteRow } from './types'

export { type NoteRow } from './types'

/**
 * Maps a SQLite NoteRow (snake_case) to a LocalNote (camelCase).
 * Fetches associated tag IDs from the note_tags join table.
 */
export function rowToLocalNote(row: NoteRow): LocalNote {
  const tagRows = query<{ tag_id: string }>(
    'SELECT tag_id FROM note_tags WHERE note_id = ?',
    [row.id],
  )
  return {
    id: row.id,
    userId: row.user_id,
    // Display titles are cleaned of markdown at read time so a title authored
    // on another client (Swift / API / MCP store the raw first line) renders as
    // plain text everywhere, matching Swift's `noteDisplayTitle`. Idempotent on
    // already-clean web titles. Purely a display projection — the stored row is
    // untouched, so this never causes sync churn. A legacy TipTap JSON title
    // (old notes stored the whole doc there) shows the title derived from it.
    title: noteTitleForDisplay(row.title),
    content: row.content,
    isPinned: Boolean(row.is_pinned),
    isEncrypted: Boolean(row.is_encrypted),
    encryptionHint: row.encryption_hint ?? undefined,
    wordCount: row.word_count,
    tags: tagRows.map((t) => t.tag_id),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    deletedAt: row.deleted_at ?? undefined,
    archivedAt: row.archived_at ?? undefined,
    sharedAt: row.shared_at,
    sortOrder: row.sort_order ?? undefined,
    isReadOnly: Boolean(row.is_read_only),
    syncStatus: row.sync_status as SyncStatus,
    ownerId: row.owner_id ?? undefined,
    ownerName: row.owner_name ?? undefined,
    role: (row.share_role ?? undefined) as LocalNote['role'],
    memberCount: row.member_count ?? 0,
    linkAccess: (row.link_access ?? undefined) as LocalNote['linkAccess'],
  }
}

/**
 * Active notes (id + display title), newest first: the `[[` mention picker and
 * the command palette. Titles go through the same read-time projection as
 * `rowToLocalNote`, so neither ever shows markdown or a raw TipTap JSON title.
 */
export function listActiveNoteTitles(): { id: string; title: string }[] {
  return query<{ id: string; title: string }>(
    'SELECT id, title FROM notes WHERE deleted_at IS NULL AND archived_at IS NULL ORDER BY updated_at DESC',
  ).map((row) => ({ id: row.id, title: noteTitleForDisplay(row.title) }))
}
